import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, from, map, Observable, of, Subject, takeUntil } from 'rxjs';
import { DialogAlertComponent } from 'src/app/modals/dialog-alert/dialog-alert.component';
import { DialogAlreadyExistsComponent } from 'src/app/modals/dialog-already-exists/dialog-already-exists.component';
import { RequestResultStatusCodeEnum } from 'src/app/Enums/RequestResultStatusCodeEnum';
import { LastRegisteredProduct } from 'src/app/models/Product/LastRegisteredProduct.model';
import { Product } from 'src/app/models/Product/ProductRegister.model';
import { ProductReport, ProductReportSelected } from 'src/app/models/Product/ProductReport.model';
import { ConfigurationCompanyProductModel } from '../../models/Configurations/ConfigurationCompanyProductModel';
import { ApiService } from '../api/api.service';
import { TranslateService } from '@ngx-translate/core';
import { UnitType } from 'src/app/models/Product/UnitTypes.model';
import { Csosn } from 'src/app/models/Taxation/Csosn.model';
import { ProductResponse } from 'src/app/models/Product/ProductResponse';
import { AlertMessageService } from '../alert-message/alert-message.service';
import { SetTaxationDialogComponent } from 'src/app/components/set-taxation/set-taxation-dialog/set-taxation-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class ProductService implements OnDestroy {

  products: ProductReport[];
  totalProducts: number = 0;
  pageSize: number = 100;
  pageSizeOptions: number[] = [100];
  pageEvent: PageEvent = new PageEvent();

  constructor(private api: ApiService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private alertService: AlertMessageService) {
    this._lastProductRegistered = new BehaviorSubject<LastRegisteredProduct>(Object.assign({}));
    this._productIdToEdit = new BehaviorSubject<number>(0);
    this.products = [];
    this.pageEvent.pageIndex = 0;
  }

  ngOnDestroy(): void {
    this._lastProductRegistered.unsubscribe();
    this._productIdToEdit.unsubscribe();
    this.destroyedProductsObservable.next();
    this.destroyedProductsObservable.complete();
  }
  //Configuration
  private _configCompanyProduct: ConfigurationCompanyProductModel[] = [];
  set setConfigCompanyProduct(config: ConfigurationCompanyProductModel[]) {
    this._configCompanyProduct = config;
  }
  get getConfigCompanyProduct(): ConfigurationCompanyProductModel[] {
    return this._configCompanyProduct;
  }

  private uncheckSubject = new BehaviorSubject<boolean>(false);

  unCheck(): void {
    this.uncheckSubject.next(true);
  }
  onUnCheck(): Observable<boolean> {
    return this.uncheckSubject.asObservable();
  }

  //Product already registered
  private _lastProductRegistered: BehaviorSubject<LastRegisteredProduct>;
  setProductRegistered(product: LastRegisteredProduct) {
    this._lastProductRegistered.next(product);
  }

  getProductRegistered(): Observable<LastRegisteredProduct> {
    return this._lastProductRegistered.asObservable();
  }
  //Product to edit
  private _productIdToEdit: BehaviorSubject<number>;
  setProductIdWillBeEdited(productId: number) {
    this._productIdToEdit.next(productId);
  }

  getProductIdWillBeEdited(): Observable<number> {
    return this._productIdToEdit.asObservable();
  }

  //products report
  private _productsReport: BehaviorSubject<ProductReport[]> = new BehaviorSubject<ProductReport[]>(Object.assign([]));
  setProducts(product: ProductReport[]) {
    this._productsReport.next(product);
  }
  getProducts(): Observable<ProductReport[]> {
    return this._productsReport.asObservable();
  }

  public async sendNewProduct(product: Product): Promise<Product | null> {
    try {

      const r = await this.api.post<Product>('/v1/product', product);

      if (r.success) {
        this.snackBar.open(r.message!, '', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['green-snackbar']
        });
        this.getListProducts(this.pageEvent.pageIndex + 1, this.pageSize);

        return r.data;

      } else {
        this.snackBar.open(r.message ?? "Internal server error", '', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['red-snackbar']
        });
        return null;
      }
    } catch (err) {
      throw (err);
    }
  }

  public async editProduct(product: Product): Promise<string> {
    try {
      const r = await this.api.put<string>('/v1/product/' + product.id, product);

      if (r.success) {
        this.snackBar.open(r.message!, '', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['green-snackbar']
        });
        this.getListProducts(this.pageEvent.pageIndex + 1, this.pageSize);
        return r.data;
      } else {
        this.snackBar.open(r.message ?? "Internal server error", '', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['red-snackbar']
        });
        return "";
      }
    } catch (err) {
      throw (err);
    }
  }

    public async setTaxationCclasstrib(cclassTribId:number,productsIds:number[]): Promise<boolean> {
    try {
      const r = await this.api.put<boolean>('/v1/product/set-tax-ibs-cbs' ,{cclassTribTaxationId:cclassTribId,productsIds:productsIds});

      if (r.success) {
        this.snackBar.open(r.message||'A regra foi aplicada com sucesso!', '', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['green-snackbar']
        });
        this.getListProducts(this.pageEvent.pageIndex + 1, this.pageSize);
        return true;
      } else {
        this.snackBar.open(r.message ?? "Internal server error", '', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['red-snackbar']
        });
        return false;
      }
    } catch (err) {
      throw (err);
    }
  }
  public async getProduct(productId: number): Promise<Product | null> {
    try {
      const r = await this.api.get<Product>('/v1/product/' + productId);
      if (r.success && r.data) {
        return r.data;
      }
      if (r.messageCode == RequestResultStatusCodeEnum.NotFound) {
        this.snackBar.open(r.message!, '', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['warning-snackbar']
        });
        return null;
      }
      throw (r.message);
    } catch (err) {
      throw (err);
    }
  }
  destroyedProductsObservable = new Subject<void>();
  public getListProducts(numberPage: number, limitpage: number, active?: boolean, manageStock?: boolean, name?: string, code?: string,ncm?:number,cest?:string,barCode?:string,unityTypeId?:number,tax?:number,taxCbsIbs?:Number) {

    return from(this.api.get<ProductResponse>('/v1/product/all-products', { numberPage, limitpage, active, manageStock, name, code,ncm: ncm || 0,barCode: barCode ? barCode : undefined,cest:cest? cest.replace(".",""):undefined, unityTypeId,tax,taxCbsIbs }))
    .pipe(
      takeUntil(this.destroyedProductsObservable),
      map(res => {
        if (!res.success) {
          if (res.message != null) this.alertService.alertWarningMessage(res.message!);
          return;
        }
        this.totalProducts = res.data.totalProducts;
        this.setProducts(res.data.allProducts);
      })
    )
    .subscribe();
  }

  public async deleteProduct(productId: number): Promise<string> {
    try {
      const r = await this.api.delete<string>('/v1/product/' + productId);
      if (r.success) {
        this.snackBar.open(r.message!, '', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['green-snackbar']
        });
        this.getListProducts(this.pageEvent.pageIndex + 1, this.pageSize);
        return r.data;
      } else if (r.messageCode == RequestResultStatusCodeEnum.MethodNotAllowed) {
        this.openDialogAlert(r.message!)
        return "";
      }
      else {
        this.snackBar.open(r.message ?? "Internal server error", '', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['red-snackbar']
        });
        return "";
      }
    } catch (err) {
      throw (err);
    }
  }

  checkIfBarCodeIsBeingUsed(barCode: string, productId?: number): Promise<Product | null> {
    return new Promise(resolve => {
      this.api.get<any>('/v1/product/check-barcode', { barCode, productId }).then(r => {
        if (r.messageCode == RequestResultStatusCodeEnum.Accepted) {
          return resolve(null)
        }
        if (r.messageCode == RequestResultStatusCodeEnum.Found) {
          this.openDialogBarCode(r.message!, this.translateService.instant("barCode.alreadyExists")).subscribe(result => {
            if (result) {
              return resolve(r.data)
            } else {
              return resolve(null)
            }
          });
          return null;
        }
        return null;
      });
    });
  }

  checkIfAlreadyExistsProductNameAsync(name: string, productId?: number): Promise<Product | null> {
    return new Promise(resolve => {
      this.api.get<any>('/v1/product/check-name', { name, productId }).then(r => {
        if (r.messageCode == RequestResultStatusCodeEnum.Accepted) {
          return resolve(null)
        }
        if (r.messageCode == RequestResultStatusCodeEnum.Found) {
          this.openDialogBarCode(r.message!, this.translateService.instant("productName.alreadyExists")).subscribe(result => {
            if (result) {
              return resolve(r.data)
            } else {
              return resolve(null)
            }
          });
          return null;
        }
        return null;
      });
    });
  }

  openDialogBarCode(message: string, title: string): Observable<boolean> {
    this.dialog.closeAll();
    const dialog = this.dialog.open(DialogAlreadyExistsComponent, {
      data: {
        title,
        message
      },
      panelClass: 'mat-dialog-alert'
    })
    return dialog.afterClosed().pipe(map(result => {
      if (result && result.includes('yes')) {
        return true;
      }
      return false;
    }))
  }

  openDialogAlert(message: string) {
    this.dialog.open(DialogAlertComponent, {
      data: {
        message: message
      },
      panelClass: 'mat-dialog-alert'
    });
  }

  openSetTaxationDialogAlert(items:ProductReportSelected[]) {
    this.dialog.open(SetTaxationDialogComponent, {
      data: items,
    width: 'auto',
    maxWidth: '90vw',
    height: 'auto',
    maxHeight: '85vh',
    panelClass: 'compact-dialog'
    });

  }
  async getUnitTypes(): Promise<UnitType[] | null> {
    const r = await this.api.get<UnitType[]>('/v1/product/unities');
    if (!r.success) {
      console.error(r);
      return null;
    }
    return r.data;
  }

  async getCsosns(): Promise<Csosn[] | null> {
    const r = await this.api.get<Csosn[]>('/v1/taxation/csosn');
    if (!r.success) {
      console.error(r);
      return null;
    }
    return r.data;
  }
}
